import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Crop } from 'react-image-crop';
import { centerCrop, convertToPercentCrop, makeAspectCrop } from 'react-image-crop';

import { useDebouncedInput } from '@/hooks/use-debounced-input';
import { useUndo } from '@/hooks/use-undo';
import { CROP_TOLERANCE_PX } from '../constants';
import type { CropState, ImageCropModalProps } from '../types';
import { coversFullImage, scaleCropToNatural } from '../utils';
import { useOriginalImage } from './use-original-image';
import { useTransformedImage } from './use-transformed-image';

const BASE_CROP_STATE = {
  crop: { unit: 'px', x: 0, y: 0, width: 0, height: 0 } as CropState['crop'],
  baseRotation: 0,
  fineRotation: 0,
  flipH: false,
  flipV: false,
  aspectRatio: undefined,
};

export function useImageCropModal({
  imageUrl,
  isOpen,
  onSave,
  onCancel,
}: Omit<ImageCropModalProps, 'showUpscale' | 'showRemoveBg'> & { imageUrl: string }) {
  const displayImgRef = useRef<HTMLImageElement>(null);
  const initialCropStateRef = useRef<CropState | null>(null);
  const hasInitializedCropRef = useRef(false);
  const cropDimensionsRef = useRef<{ width: number; height: number } | null>(null);
  const prevIsOpenRef = useRef(false);
  const prevImageUrlRef = useRef(imageUrl);

  const { state, setState, undo, redo, canUndo, canRedo } = useUndo<CropState>({
    ...BASE_CROP_STATE,
    imageUrl,
  });
  const { originalImgRef, originalLoaded } = useOriginalImage(state.imageUrl, isOpen);
  const totalRotation = state.baseRotation + state.fineRotation;
  const { transformedImageUrl, transformedDimensions, isTransforming } = useTransformedImage(
    originalLoaded,
    originalImgRef,
    totalRotation,
    state.flipH,
    state.flipV,
  );

  const [crop, setCrop] = useState<Crop>();
  const [isSaving, setIsSaving] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);

  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    const prevImageUrl = prevImageUrlRef.current;
    prevIsOpenRef.current = isOpen;
    prevImageUrlRef.current = imageUrl;

    if (!isOpen || !imageUrl) {
      initialCropStateRef.current = null;
      hasInitializedCropRef.current = false;
      cropDimensionsRef.current = null;
      return;
    }

    if (!wasOpen || (wasOpen && imageUrl !== prevImageUrl)) {
      hasInitializedCropRef.current = false;
      cropDimensionsRef.current = null;
      setState({ ...BASE_CROP_STATE, imageUrl }, { replace: true });
    }
  }, [isOpen, imageUrl, setState]);

  const selectedAspectRatio = useMemo((): number | null | undefined => {
    if (state.aspectRatio !== undefined) return state.aspectRatio;
    const img = displayImgRef.current;
    if (!img?.offsetWidth || !img?.offsetHeight) return undefined;
    return coversFullImage(state.crop, img.offsetWidth, img.offsetHeight, CROP_TOLERANCE_PX)
      ? null
      : undefined;
  }, [state.aspectRatio, state.crop]);

  const createCenteredCrop = useCallback(
    (imgWidth: number, imgHeight: number, aspect?: number): CropState['crop'] => {
      if (!aspect) {
        return centerCrop({ unit: 'px', width: imgWidth, height: imgHeight }, imgWidth, imgHeight);
      }
      return centerCrop(
        makeAspectCrop(
          { unit: 'px', width: Math.min(imgWidth, imgHeight * aspect) },
          aspect,
          imgWidth,
          imgHeight,
        ),
        imgWidth,
        imgHeight,
      );
    },
    [],
  );

  const [rotationSliderValue, setRotationSliderValue] = useDebouncedInput(totalRotation, (total) =>
    setState((prev) => ({ ...prev, fineRotation: total - prev.baseRotation })),
  );

  const handleTransformedImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      setIsImageLoading(false);
      const img = e.currentTarget;
      const width = img.offsetWidth;
      const height = img.offsetHeight;

      if (!hasInitializedCropRef.current) {
        const initialCrop = createCenteredCrop(width, height, state.aspectRatio);
        const initialState: CropState = {
          ...BASE_CROP_STATE,
          imageUrl: state.imageUrl,
          aspectRatio: state.aspectRatio,
          crop: initialCrop,
        };
        setState(initialState, { replace: true });
        initialCropStateRef.current = initialState;
        setCrop(convertToPercentCrop(initialCrop, width, height));
        hasInitializedCropRef.current = true;
        cropDimensionsRef.current = { width, height };
      } else {
        const prevDim = cropDimensionsRef.current;
        const dimensionsChanged = !prevDim || prevDim.width !== width || prevDim.height !== height;

        if (dimensionsChanged) {
          const shouldUseCenteredCropWithAspect =
            !prevDim ||
            coversFullImage(state.crop, prevDim.width, prevDim.height, CROP_TOLERANCE_PX);
          const hasPresetAspect = state.aspectRatio !== undefined;

          let pixelCrop: CropState['crop'];

          if (hasPresetAspect || shouldUseCenteredCropWithAspect) {
            pixelCrop = createCenteredCrop(width, height, state.aspectRatio);
          } else {
            const { x, y, width: cw, height: ch } = state.crop;
            const relX = x / prevDim.width;
            const relY = y / prevDim.height;
            const relW = cw / prevDim.width;
            const relH = ch / prevDim.height;
            const cwNew = Math.round(width * relW);
            const chNew = Math.round(height * relH);
            const x1 = Math.round(width * relX);
            const y1 = Math.round(height * relY);
            pixelCrop = {
              unit: 'px',
              x: Math.max(0, Math.min(x1, width - cwNew)),
              y: Math.max(0, Math.min(y1, height - chNew)),
              width: Math.max(1, Math.min(cwNew, width)),
              height: Math.max(1, Math.min(chNew, height)),
            };
          }

          setState((prev) => ({ ...prev, crop: pixelCrop }), {
            skipHistory: true,
          });
          setCrop(convertToPercentCrop(pixelCrop, width, height));
          cropDimensionsRef.current = { width, height };
        } else {
          setCrop(convertToPercentCrop(state.crop, width, height));
        }
      }
    },
    [createCenteredCrop, setState, state.aspectRatio, state.crop, state.imageUrl],
  );

  useEffect(() => {
    const img = displayImgRef.current;
    if (!img?.complete || img.naturalWidth === 0) return;
    const w = img.offsetWidth;
    const h = img.offsetHeight;
    if (w === 0 || h === 0) return;
    setCrop(convertToPercentCrop(state.crop, w, h));
  }, [state.crop]);

  const handleAspectRatioChange = useCallback(
    (ratio: number | null) => {
      const newAspect = ratio === null ? undefined : ratio;
      const img = displayImgRef.current;
      if (!img) return;

      const width = img.offsetWidth;
      const height = img.offsetHeight;
      const newCrop = createCenteredCrop(width, height, newAspect);
      setState((prev) => ({ ...prev, aspectRatio: newAspect, crop: newCrop }));
      setCrop(convertToPercentCrop(newCrop, width, height));
    },
    [createCenteredCrop, setState],
  );

  const handleRotate90 = useCallback(
    (direction: 'left' | 'right') => {
      const degrees = direction === 'left' ? -90 : 90;
      setState((prev) => ({
        ...prev,
        baseRotation: (prev.baseRotation + degrees + 360) % 360,
        fineRotation: 0,
      }));
    },
    [setState],
  );

  const handleFlip = useCallback(
    (direction: 'horizontal' | 'vertical') => {
      setState((prev) => ({
        ...prev,
        flipH: direction === 'horizontal' ? !prev.flipH : prev.flipH,
        flipV: direction === 'vertical' ? !prev.flipV : prev.flipV,
      }));
    },
    [setState],
  );

  const handleRevert = useCallback(() => {
    const initial = initialCropStateRef.current;
    if (!initial) return;
    setState(initial, { replace: true });
    const img = displayImgRef.current;
    if (img?.complete && img.naturalWidth > 0 && img.offsetWidth > 0 && img.offsetHeight > 0) {
      cropDimensionsRef.current = { width: img.offsetWidth, height: img.offsetHeight };
      setCrop(convertToPercentCrop(initial.crop, img.offsetWidth, img.offsetHeight));
    }
  }, [setState]);

  const handleCropChange = useCallback((_: Crop, newCrop: Crop) => {
    if (newCrop.width > 0 && newCrop.height > 0) setCrop(newCrop);
  }, []);

  const handleCropComplete = useCallback(
    (pixelCrop: CropState['crop']) => {
      const valid = pixelCrop.width > 0 && pixelCrop.height > 0;
      if (!valid) return;

      if (state.crop.width === 0 && state.crop.height === 0) return;

      const cropChanged =
        Math.round(state.crop.width) !== Math.round(pixelCrop.width) ||
        Math.round(state.crop.height) !== Math.round(pixelCrop.height) ||
        Math.round(state.crop.x) !== Math.round(pixelCrop.x) ||
        Math.round(state.crop.y) !== Math.round(pixelCrop.y);

      setState(
        (prev) => ({
          ...prev,
          crop: pixelCrop,
          aspectRatio: cropChanged ? undefined : prev.aspectRatio,
        }),
        { skipHistory: !cropChanged },
      );
    },
    [setState, state.crop],
  );

  const handleSave = useCallback(async () => {
    const img = displayImgRef.current;
    if (!img) return;

    const scaledCrop = scaleCropToNatural(
      state.crop,
      img.offsetWidth,
      img.offsetHeight,
      transformedDimensions.width,
      transformedDimensions.height,
    );

    setIsSaving(true);
    try {
      await onSave({
        imageUrl: state.imageUrl,
        croppedAreaPixels: scaledCrop,
        rotation: totalRotation,
        flipHorizontal: state.flipH,
        flipVertical: state.flipV,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    state.imageUrl,
    state.crop,
    totalRotation,
    state.flipH,
    state.flipV,
    transformedDimensions,
    onSave,
  ]);

  const applyTransformUrl = useCallback(
    (newUrl: string) => {
      setState((prev) => ({ ...prev, imageUrl: newUrl }));
    },
    [setState],
  );

  return {
    displayImgRef,
    crop,
    state,
    transformedImageUrl,
    isImageLoading,
    isTransforming,
    isSaving,
    selectedAspectRatio,
    rotationSliderValue,
    setRotationSliderValue,
    handleTransformedImageLoad,
    handleAspectRatioChange,
    handleRotate90,
    handleFlip,
    handleRevert,
    handleCropChange,
    handleCropComplete,
    handleSave,
    applyTransformUrl,
    undo,
    redo,
    canUndo,
    canRedo,
    onCancel,
  };
}
